/* eslint-disable indent */
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  changeUser,
  setActiveNationalTeam,
  setLanguage,
  setLayoutWidth,
  initializeUser,
} from '../reducers/userReducer'
import { resetPlayers } from '../reducers/playerReducer'
import { clearAllCandidates } from '../reducers/candidateReducer'
import { clearAllLists } from '../reducers/listReducer'
import { resetAllPlayers } from '../reducers/allPlayersReducer'
import { resetFilter } from '../reducers/filterReducer'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Dropdown from 'react-bootstrap/Dropdown'
import { getTranslation, getSupportedLanguages } from '../services/translations'

// Uudet alikomponentit
import AdminTools from './AdminTools'
import NationalTeamSelector from './NationalTeamSelector'
import TeamVisibilityManager from './TeamVisibilityManager'
import StaffManagement from './StaffManagement'

const Login = () => {
  const dispatch = useDispatch()
  const user = useSelector(state => state.user)
  const lang = (user && user.language) || 'fi'
  const layoutWidth = (user && user.layoutWidth) || 'normal'
  const t = key => getTranslation(lang, key)

  const [message, setMessage] = useState({ text: '', type: '' })
  const [staffData, setStaffData] = useState(null)
  const [teamStaffMembers, setTeamStaffMembers] = useState([])
  const [loadingStaff, setLoadingStaff] = useState(false)
  const [fixPlayerId, setFixPlayerId] = useState('')
  const [rollbackPlayerId, setRollbackPlayerId] = useState('')
  const [recalc99PlayerId, setRecalc99PlayerId] = useState('')
  const [refreshingTeams, setRefreshingTeams] = useState(false)

  // Hae kayttajatiedot Reduxista (tai backendistä jos ei ole cachessa)
  useEffect(() => {
    dispatch(initializeUser())
  }, [dispatch])

  // Synkronoi Staff NationalTeams-tietojen perusteella
  useEffect(() => {
    if (user && user.oauth_token) {
      axios
        .post(`/users/staff/sync/${user.oauth_token}`)
        .then(response => {
          setStaffData(response.data)
          dispatch(
            changeUser({
              ...user,
              staff: response.data.staff,
              activeNationalTeam: response.data.activeNationalTeam,
            })
          )
        })
        .catch(error => console.error('Error syncing staff:', error))
    }
  }, [user && user.oauth_token])

  // Tarkista onko taskId cookieissa (päivitys käynnissä)
  useEffect(() => {
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.trim().split('=')
      acc[key] = value
      return acc
    }, {})
    if (cookies.taskId) {
      displayMessage(t('updateInProgress'), 'info', true, true)
      checkTaskStatus(cookies.taskId, t('updateSuccess'), t('updateFailed'))
    }
  }, [])

  // Hae Staff-lista automaattisesti kun staffData on asetettu
  useEffect(() => {
    if (staffData && staffData.activeNationalTeam && user && user.oauth_token) {
      fetchTeamStaffMembers(staffData.activeNationalTeam)
    }
  }, [staffData && staffData.activeNationalTeam])

  // Tarkista onko teams vanhassa muodossa ja päivitä tarvittaessa
  useEffect(() => {
    const hasOldTeamsFormat = () => {
      if (!user || !user.teams || user.teams.length === 0) return false
      // Vanha muoto: array sisältää suoraan numeroita [12345, 67890]
      // Uusi muoto: array sisältää objekteja [{teamID: 12345, teamName: "..."}, ...]
      return typeof user.teams[0] !== 'object'
    }

    const refreshUserData = async () => {
      try {
        console.log(
          'Havaittu vanha teams-muoto, päivitetään käyttäjän tiedot...'
        )
        displayMessage('Päivitetään käyttäjätietoja...', 'info', true, true)
        const response = await axios.get(
          `/login/refresh-user-data/${user.oauth_token}`
        )
        // Päivitä localStorage uudella oauth_tokenilla
        window.localStorage.setItem(
          'loggedUser',
          JSON.stringify(response.data.oauth_token)
        )
        const refreshedUser = await axios.get(
          `/users/${response.data.oauth_token}`
        )
        dispatch(changeUser(refreshedUser.data))
        displayMessage('Käyttäjätiedot päivitetty', 'success')
      } catch (error) {
        console.error('Virhe käyttäjätietojen päivityksessä:', error)
        displayMessage('Käyttäjätietojen päivitys epäonnistui', 'danger')
      }
    }

    if (user && user.oauth_token && hasOldTeamsFormat()) {
      refreshUserData()
    }
  }, [user && user.oauth_token && user.teams])

  // === Viestien naytto ===
  const displayMessage = (
    text,
    type,
    persistent = false,
    showSpinner = false
  ) => {
    setMessage({ text, type, showSpinner })
    if (!persistent) {
      setTimeout(
        () => setMessage({ text: '', type: '', showSpinner: false }),
        2000
      )
    }
  }

  // === Maajoukkueen vaihto ===
  const handleNationalTeamChange = async (nationalTeamId, nationalTeamName) => {
    try {
      await axios.put(`/users/active-team/${user.oauth_token}`, {
        nationalTeamId,
      })
      dispatch(setActiveNationalTeam(nationalTeamId))
      // Nollaa kaikki storet paitsi user kun maa vaihtuu
      dispatch(resetPlayers())
      dispatch(clearAllCandidates())
      dispatch(clearAllLists())
      dispatch(resetAllPlayers())
      dispatch(resetFilter())
      setStaffData({
        ...staffData,
        activeNationalTeam: nationalTeamId,
      })
      displayMessage(`${t('countryChanged')}: ${nationalTeamName}`, 'success')
      fetchTeamStaffMembers(nationalTeamId)
    } catch (error) {
      console.error('Error changing national team:', error)
      displayMessage(t('countryChangeFailed'), 'danger')
    }
  }

  // === Staff-hallinta ===
  const fetchTeamStaffMembers = async nationalTeamId => {
    setLoadingStaff(true)
    try {
      const response = await axios.get(
        `/users/staff/team/${nationalTeamId}?oauth_token=${user.oauth_token}`
      )
      setTeamStaffMembers(response.data)
    } catch (error) {
      console.error('Error fetching team staff:', error)
      setTeamStaffMembers([])
    } finally {
      setLoadingStaff(false)
    }
  }

  const handleGrantStaffRole = async (
    selectedUser,
    selectedRole,
    selectedNationalTeam
  ) => {
    if (!selectedUser || !selectedRole || !selectedNationalTeam) {
      displayMessage(t('selectUserRoleCountry'), 'warning')
      return
    }

    try {
      await axios.post('/users/staff/grant', {
        grantorOauthToken: user.oauth_token,
        targetUserId: selectedUser.id,
        nationalTeamId: selectedNationalTeam.nationalTeamId,
        role: selectedRole,
      })
      displayMessage(
        `${t('roleGranted')}: ${selectedUser.LoginName}`,
        'success'
      )
      fetchTeamStaffMembers(selectedNationalTeam.nationalTeamId)
    } catch (error) {
      console.error('Error granting staff role:', error)
      displayMessage(
        (error.response && error.response.data && error.response.data.error) ||
          t('roleGrantFailed'),
        'danger'
      )
    }
  }

  const handleRevokeStaffRole = async (targetUserId, nationalTeamId) => {
    if (!window.confirm(t('confirmRemoveRole'))) return

    try {
      await axios.delete(
        `/users/staff/revoke/${targetUserId}/${nationalTeamId}?oauth_token=${user.oauth_token}`
      )
      displayMessage(t('roleRemoved'), 'success')
      fetchTeamStaffMembers(nationalTeamId)
    } catch (error) {
      console.error('Error revoking staff role:', error)
      displayMessage(t('roleRemoveFailed'), 'danger')
    }
  }

  // === Admin-toiminnot ===
  const syncNations = async () => {
    try {
      displayMessage('Synkronoidaan maatietoja...', 'info', true, true)
      const response = await axios.post(`/api/nations/sync/${user.oauth_token}`)
      displayMessage(
        `${t('nationSyncSuccess')}: ${response.data.created} new, ${
          response.data.updated
        } updated`,
        'success'
      )
    } catch (error) {
      console.error('Error syncing nations:', error)
      displayMessage(t('nationSyncFailed'), 'danger')
    }
  }

  const updateAll = async () => {
    await handleTaskAction(
      `/login/updateall/${user.oauth_token}`,
      t('allPlayersUpdateSuccess'),
      t('allPlayersUpdateFailed')
    )
  }

  const refreshMissingTeams = async () => {
    await handleTaskAction(
      `/login/refresh-missing-teams/${user.oauth_token}`,
      t('missingTeamsRefreshSuccess'),
      t('missingTeamsRefreshFailed')
    )
  }

  const cleanupEmptyUsers = async () => {
    if (!window.confirm('Poistetaanko varmennetut tyhjät käyttäjät?')) return
    await handleTaskAction(
      `/login/cleanup-empty-users/${user.oauth_token}`,
      'Tyhjät käyttäjät siivottu!',
      'Tyhjien käyttäjien siivous epäonnistui'
    )
  }

  const updateHanni = async () => {
    const response = await axios.get(
      '/api/achievements/hanni/' + user.oauth_token + '/1003'
    )
    if (response.status === 200) {
      displayMessage(response.data.message, 'success')
    } else {
      displayMessage(t('hanniUpdateFailed'), 'danger')
    }
  }

  const updateHanniItaly = async () => {
    const response = await axios.get(
      '/api/achievements/hanni/' + user.oauth_token + '/4'
    )
    if (response.status === 200) {
      displayMessage(response.data.message, 'success')
    } else {
      displayMessage(t('hanniUpdateFailed'), 'danger')
    }
  }

  // Kaikkien pelaajien fix-päivitys (vain admin)
  const fixAll = async () => {
    await handleTaskAction(
      `/login/fixall/${user.oauth_token}`,
      'Kaikkien pelaajien fix-päivitys onnistui!',
      'Kaikkien pelaajien fix-päivitys epäonnistui'
    )
  }

  // Omien pelaajien fix-päivitys
  const fixMyPlayers = async () => {
    await handleTaskAction(
      `/login/fixplayers/${user.oauth_token}`,
      'Fix My Players onnistui!',
      'Fix My Players epäonnistui'
    )
  }

  // Yksittäisen pelaajan fix-päivitys
  const fixSinglePlayer = async () => {
    if (!fixPlayerId || fixPlayerId.trim() === '') {
      displayMessage('Anna pelaajan ID', 'warning')
      return
    }
    try {
      displayMessage('Päivitetään pelaajaa...', 'info', true, true)
      const response = await axios.get(
        `/login/fixsingleplayer/${user.oauth_token}:${fixPlayerId.trim()}`
      )
      if (response.status === 200) {
        displayMessage(`Pelaaja ${fixPlayerId} päivitetty!`, 'success')
        setFixPlayerId('')
      }
    } catch (error) {
      console.error('Error fixing single player:', error)
      displayMessage(`Pelaajan ${fixPlayerId} päivitys epäonnistui`, 'danger')
    }
  }

  // === Recalc-99 ===
  const recalc99Single = async () => {
    if (!recalc99PlayerId || recalc99PlayerId.trim() === '') {
      displayMessage('Anna pelaajan ID', 'warning')
      return
    }
    try {
      displayMessage('Recalc-99 käynnissä...', 'info', true, true)
      const response = await axios.post(
        `/api/players/recalc-99/${recalc99PlayerId.trim()}`,
        { id: user.oauth_token }
      )
      if (response.data.recalced) {
        displayMessage(
          `Recalc-99: ${response.data.name} - ${response.data.trainings} harjoitusta`,
          'success'
        )
        setRecalc99PlayerId('')
      } else {
        displayMessage(`Recalc-99: ${response.data.message}`, 'info')
      }
    } catch (error) {
      console.error('Recalc-99 error:', error)
      displayMessage('Recalc-99 epäonnistui', 'danger')
    }
  }

  const recalc99All = async () => {
    try {
      displayMessage('Recalc-99 kaikille käynnissä...', 'info', true, true)
      const response = await axios.post('/api/players/recalc-99', {
        id: user.oauth_token,
      })
      displayMessage(
        `Recalc-99: ${response.data.message}`,
        'success',
        false,
        false
      )
    } catch (error) {
      console.error('Recalc-99 all error:', error)
      displayMessage('Recalc-99 epäonnistui', 'danger')
    }
  }

  // === Rollback ===
  const rollbackSingle = async () => {
    if (!rollbackPlayerId || rollbackPlayerId.trim() === '') {
      displayMessage('Anna pelaajan ID', 'warning')
      return
    }
    try {
      displayMessage('Rollback käynnissä...', 'info', true, true)
      const response = await axios.post(
        `/api/players/rollback/${rollbackPlayerId.trim()}`,
        { id: user.oauth_token }
      )
      if (response.data.rolled) {
        displayMessage(`Rollback: ${response.data.message}`, 'success')
        setRollbackPlayerId('')
      } else {
        displayMessage(`Rollback ohitettu: ${response.data.message}`, 'info')
      }
    } catch (error) {
      console.error('Rollback error:', error)
      displayMessage('Rollback epäonnistui', 'danger')
    }
  }

  const rollbackMyTeam = async () => {
    await handleTaskAction(
      `/login/rollback-myteam/${user.oauth_token}`,
      'Oman joukkueen rollback onnistui!',
      'Oman joukkueen rollback epäonnistui'
    )
  }

  const rollbackAll = async () => {
    await handleTaskAction(
      `/login/rollback-all/${user.oauth_token}`,
      'Kaikkien pelaajien rollback onnistui!',
      'Kaikkien pelaajien rollback epäonnistui'
    )
  }

  // === Task-hallinta ===
  const handleTaskAction = async (url, successMessage, failureMessage) => {
    try {
      console.log('[task] request', { url })
      const response = await axios.get(url)
      console.log('[task] response', {
        url,
        status: response.status,
        taskId: response?.data?.taskId,
        data: response?.data,
      })
      if (response.status === 200) {
        displayMessage(t('updateInProgress'), 'info', true, true)
        await checkTaskStatus(
          response.data.taskId,
          successMessage,
          failureMessage
        )
      } else {
        displayMessage(failureMessage, 'danger')
      }
    } catch (error) {
      console.error('Error executing task:', error)
      console.error('[task] request failed', {
        url,
        error: error?.response?.data || error?.message,
      })
      const backendError =
        error?.response?.data?.error || error?.response?.data?.message || ''
      displayMessage(
        backendError ? `${failureMessage}: ${backendError}` : failureMessage,
        'danger',
        true,
        false
      )
    }
  }

  const checkTaskStatus = async (taskId, successMessage, failureMessage) => {
    try {
      const statusResponse = await axios.get(`/login/task-status/${taskId}`)
      console.log('[task] status', {
        taskId,
        status: statusResponse?.data?.status,
        data: statusResponse?.data,
      })
      if (statusResponse.data.status === 'completed') {
        const { updatedCount, updatedNames } = statusResponse.data
        if (updatedCount !== undefined && updatedNames) {
          const names = updatedNames.join(', ')
          displayMessage(
            `${successMessage} (${updatedCount} ${t('players')}: ${names})`,
            'success',
            false,
            false
          )
        } else {
          displayMessage(successMessage, 'success', false, false)
        }
      } else if (statusResponse.data.status === 'failed') {
        console.error('Task failed:', statusResponse.data.error)
        const reason = statusResponse.data.error
          ? `${failureMessage}: ${statusResponse.data.error}`
          : failureMessage
        displayMessage(reason, 'danger', true, false)
      } else {
        setTimeout(
          () => checkTaskStatus(taskId, successMessage, failureMessage),
          1000
        )
      }
    } catch (error) {
      console.error('Error checking task status:', error)
      displayMessage(failureMessage, 'danger', false, false)
    }
  }

  // === Joukkueen näkyvyyden hallinta ===
  const handleTeamToggle = async (teamId, currentIsPublic) => {
    const isNewFormat =
      user.teams.length > 0 && typeof user.teams[0] === 'object'

    let updatedTeams

    if (isNewFormat) {
      updatedTeams = user.teams.map(team =>
        team.teamID === teamId
          ? {
              ...team,
              public: !currentIsPublic,
              hideTrainingData: currentIsPublic
                ? team.hideTrainingData
                : false,
            }
          : team
      )
    } else {
      updatedTeams = user.teams.map(id =>
        id === teamId
          ? { teamID: id, public: !currentIsPublic, teamName: '' }
          : { teamID: id, public: true, teamName: '' }
      )
    }

    try {
      await axios.put(`/users/${user.oauth_token}`, {
        ...user,
        teams: updatedTeams,
      })
      dispatch(changeUser({ ...user, teams: updatedTeams }))
      displayMessage(t('teamVisibilityUpdated'), 'success')
    } catch (error) {
      console.error('Error updating team visibility:', error)
      displayMessage(t('teamVisibilityFailed'), 'danger')
    }
  }

  // === Kielenvaihto ===
  const handleLanguageChange = newLang => {
    dispatch(setLanguage(newLang))
  }

  const handleLayoutWidthChange = widthMode => {
    dispatch(setLayoutWidth(widthMode))
  }

  // === Login/Logout ===
  const logIn = async () => {
    try {
      const response = await axios.get('/login')
      window.localStorage.setItem(
        'loggedUser',
        JSON.stringify(response.data.substring(59))
      )
      window.location = response.data
    } catch (error) {
      console.error('Error logging in:', error)
    }
  }

  const logOut = () => {
    window.localStorage.clear()
    dispatch(changeUser(null))
  }

  const handleHideTrainingDataToggle = async (teamId, hideTrainingData) => {
    const updatedTeams = user.teams.map(team => {
      if (typeof team !== 'object') {
        return {
          teamID: team,
          public: true,
          teamName: '',
          hideTrainingData: false,
        }
      }

      return team.teamID === teamId
        ? { ...team, hideTrainingData: !hideTrainingData }
        : team
    })

    try {
      await axios.put(`/users/${user.oauth_token}`, {
        ...user,
        teams: updatedTeams,
      })
      dispatch(changeUser({ ...user, teams: updatedTeams }))
      displayMessage(t('teamVisibilityUpdated'), 'success')
    } catch (error) {
      console.error('Error updating training data visibility:', error)
      displayMessage(t('teamVisibilityFailed'), 'danger')
    }
  }

  const refreshTeams = async () => {
    try {
      setRefreshingTeams(true)
      const response = await axios.get(
        `/login/refresh-user-data/${user.oauth_token}`
      )
      window.localStorage.setItem(
        'loggedUser',
        JSON.stringify(response.data.oauth_token)
      )
      const refreshedUser = await axios.get(
        `/users/${response.data.oauth_token}`
      )
      dispatch(changeUser(refreshedUser.data))
      displayMessage(t('teamsRefreshSuccess'), 'success')
    } catch (error) {
      console.error('Error refreshing teams:', error)
      displayMessage(t('teamsRefreshFailed'), 'danger')
    } finally {
      setRefreshingTeams(false)
    }
  }

  const refreshLoginCredentials = async event => {
    event.preventDefault()

    try {
      await axios.post('/users/logout-all', {
        oauth_token: user.oauth_token,
      })
      logOut()
      await logIn()
    } catch (error) {
      console.error('Error refreshing login credentials:', error)
      displayMessage(t('logoutAllFailed'), 'danger')
    }
  }

  return (
    <Card
      style={{
        backgroundColor: '#2C2F33',
        color: '#FFFFFF',
        borderColor: '#1E2124',
      }}
    >
      <Card.Body>
        {!user?.oauth_token && (
          <Row className="justify-content-center mb-4">
            <Col md={8} className="text-center">
              <h2>{t('welcomeTitle')}</h2>
              <Card.Text>
                {t('welcomeText')}
              </Card.Text>
            </Col>
          </Row>
        )}
        <Row>
          {/* Vasen sarake - Joukkueet ja admin */}
          <Col md={3}>
            {user && user.oauth_token && (
              <>
                <TeamVisibilityManager
                  user={user}
                  handleTeamToggle={handleTeamToggle}
                  handleHideTrainingDataToggle={handleHideTrainingDataToggle}
                  lang={lang}
                />
                <Button
                  className="mt-2"
                  variant="outline-light"
                  size="sm"
                  onClick={refreshTeams}
                  disabled={refreshingTeams}
                >
                  {refreshingTeams ? (
                    <>
                      <Spinner size="sm" className="me-1" />
                      {t('teamsRefreshing')}
                    </>
                  ) : (
                    t('refreshTeams')
                  )}
                </Button>
                <AdminTools
                  user={user}
                  updateAll={updateAll}
                  refreshMissingTeams={refreshMissingTeams}
                  cleanupEmptyUsers={cleanupEmptyUsers}
                  updateHanni={updateHanni}
                  updateHanniItaly={updateHanniItaly}
                  syncNations={syncNations}
                  fixAll={fixAll}
                  fixMyPlayers={fixMyPlayers}
                  fixSinglePlayer={fixSinglePlayer}
                  fixPlayerId={fixPlayerId}
                  setFixPlayerId={setFixPlayerId}
                  rollbackSingle={rollbackSingle}
                  rollbackMyTeam={rollbackMyTeam}
                  rollbackAll={rollbackAll}
                  rollbackPlayerId={rollbackPlayerId}
                  setRollbackPlayerId={setRollbackPlayerId}
                  recalc99Single={recalc99Single}
                  recalc99All={recalc99All}
                  recalc99PlayerId={recalc99PlayerId}
                  setRecalc99PlayerId={setRecalc99PlayerId}
                  lang={lang}
                />
              </>
            )}
          </Col>

          {/* Keskimmainen sarake - Maajoukkue ja Staff-hallinta */}
          <Col md={4}>
            {user && user.oauth_token && (
              <>
                <NationalTeamSelector
                  staffData={staffData}
                  handleNationalTeamChange={handleNationalTeamChange}
                  lang={lang}
                />
                <StaffManagement
                  user={user}
                  staffData={staffData}
                  fetchTeamStaffMembers={fetchTeamStaffMembers}
                  handleGrantStaffRole={handleGrantStaffRole}
                  handleRevokeStaffRole={handleRevokeStaffRole}
                  teamStaffMembers={teamStaffMembers}
                  loadingStaff={loadingStaff}
                  lang={lang}
                />
              </>
            )}
          </Col>

          {/* Oikea sarake - Login/Logout, päivitysnapit ja kielivalinta */}
          <Col md={5} className="text-end">
            {user && user.oauth_token ? (
              <>
                <div className="d-flex justify-content-end align-items-center gap-2 mb-2">
                  <Button
                    variant="dark"
                    onClick={() =>
                      handleTaskAction(
                        `/login/updateplayers/${user.oauth_token}`,
                        t('updateSuccess'),
                        t('updateFailed')
                      )
                    }
                  >
                    {t('manualUpdate')}
                  </Button>
                  <Button variant="danger" onClick={logOut}>
                    {t('logout')}
                  </Button>
                  <Dropdown>
                    <Dropdown.Toggle variant="secondary" size="sm">
                      {layoutWidth === 'full'
                        ? t('layoutFullOption')
                        : layoutWidth === 'wide'
                        ? t('layoutWideOption')
                        : t('layoutNormalOption')}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={() => handleLayoutWidthChange('normal')}
                        active={layoutWidth === 'normal'}
                      >
                        {t('layoutNormalOption')}
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleLayoutWidthChange('wide')}
                        active={layoutWidth === 'wide'}
                      >
                        {t('layoutWideOption')}
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleLayoutWidthChange('full')}
                        active={layoutWidth === 'full'}
                      >
                        {t('layoutFullOption')}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown>
                    <Dropdown.Toggle variant="secondary" size="sm">
                      {lang.toUpperCase()}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {getSupportedLanguages().map(language => (
                        <Dropdown.Item
                          key={language.code}
                          onClick={() => handleLanguageChange(language.code)}
                          active={lang === language.code}
                        >
                          {language.name}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <Card.Text
                  className="text-start mt-3"
                  style={{ fontSize: '0.9rem' }}
                >
                  {t('infoText')} {t('refreshLoginInfoText')}{' '}
                  <a href="/" onClick={refreshLoginCredentials}>
                    {t('refreshLoginCredentials')}
                  </a>
                  .
                </Card.Text>
              </>
            ) : (
              <div className="d-flex justify-content-end align-items-center gap-2">
                <Button variant="dark" onClick={logIn}>
                  {t('login')}
                </Button>
                <Dropdown>
                  <Dropdown.Toggle variant="secondary" size="sm">
                    {layoutWidth === 'full'
                      ? t('layoutFullOption')
                      : layoutWidth === 'wide'
                      ? t('layoutWideOption')
                      : t('layoutNormalOption')}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => handleLayoutWidthChange('normal')}
                      active={layoutWidth === 'normal'}
                    >
                      {t('layoutNormalOption')}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => handleLayoutWidthChange('wide')}
                      active={layoutWidth === 'wide'}
                    >
                      {t('layoutWideOption')}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => handleLayoutWidthChange('full')}
                      active={layoutWidth === 'full'}
                    >
                      {t('layoutFullOption')}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <Dropdown>
                  <Dropdown.Toggle variant="secondary" size="sm">
                    {lang.toUpperCase()}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {getSupportedLanguages().map(language => (
                      <Dropdown.Item
                        key={language.code}
                        onClick={() => handleLanguageChange(language.code)}
                        active={lang === language.code}
                      >
                        {language.name}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            )}
          </Col>
        </Row>

        {/* Viesti-alue */}
        {message.text && (
          <Row>
            <Col>
              <Alert
                variant={message.type}
                className="mt-3"
                style={{ whiteSpace: 'pre-wrap' }}
              >
                {message.showSpinner && (
                  <Spinner animation="border" size="sm" className="me-2" />
                )}
                {message.text}
              </Alert>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  )
}

export default Login
